import { Dimensions, Platform } from "react-native";

/**
 * 2D coordinates representing an anchor point (e.g. cursor or trigger element).
 */
export interface IPopoverAnchor {
  x: number;
  y: number;
}

/**
 * Options for calculating contextual popover geometry and placement.
 */
export interface ICalculatePopoverOptions {
  /** Optional contextual anchor position (e.g. cursor coordinates or trigger bounds) */
  anchorPosition?: IPopoverAnchor | null;
  /** Ideal target width for the popover card */
  popoverWidth?: number;
  /** Ideal target height for the popover card */
  popoverHeight?: number;
  /** Optional viewport width override */
  windowWidth?: number;
  /** Optional viewport height override */
  windowHeight?: number;
  /** Minimum distance in pixels from viewport edge */
  margin?: number;
  /** Vertical gap between anchor point and popover */
  offset?: number;
}

/**
 * Computed position and sizing for a floating contextual popover.
 */
export interface IPopoverLayout {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
  opensUpward: boolean;
}

/**
 * Safely extracts client/viewport coordinates from a click/touch event,
 * correctly handling web scroll offsets so popovers anchor accurately.
 *
 * @param {any} e - Native or synthetic event from onPress.
 * @returns {IPopoverAnchor | null} The resolved anchor coordinates or null.
 */
export function extractAnchorCoordinates(e: any): IPopoverAnchor | null {
  if (!e) return null;
  const native = e.nativeEvent || e;
  let x: number | undefined;
  let y: number | undefined;

  if (Platform.OS === "web") {
    x = native?.clientX ?? native?.pageX;
    y = native?.clientY ?? native?.pageY;
  } else {
    x = native?.pageX ?? native?.clientX;
    y = native?.pageY ?? native?.clientY;
  }

  if (typeof x === "number" && typeof y === "number" && !isNaN(x) && !isNaN(y) && (x !== 0 || y !== 0)) {
    return { x, y };
  }
  return null;
}

/**
 * Calculates absolute viewport coordinates for a floating hover popover anchored to a cursor or trigger.
 * Automatically handles viewport boundary clamping and upward vs downward opening orientation.
 *
 * @param {ICalculatePopoverOptions} [options={}] - Configuration options.
 * @returns {IPopoverLayout} Computed top, left, width, maxHeight, and opening direction.
 */
export function calculatePopoverPosition(
  options: ICalculatePopoverOptions = {}
): IPopoverLayout {
  const windowDimensions = Dimensions.get("window");
  const windowWidth =
    options.windowWidth ??
    (Platform.OS === "web" && typeof window !== "undefined" && window.innerWidth
      ? window.innerWidth
      : windowDimensions.width);
  const windowHeight =
    options.windowHeight ??
    (Platform.OS === "web" && typeof window !== "undefined" && window.innerHeight
      ? window.innerHeight
      : windowDimensions.height);

  const margin = options.margin ?? 12;
  const offset = options.offset ?? 8;
  const requestedWidth = options.popoverWidth ?? 440;
  const requestedHeight = options.popoverHeight ?? 480;

  // Clamp width and height to available viewport space
  const width = Math.min(requestedWidth, Math.max(260, windowWidth - margin * 2));
  const maxHeight = Math.min(requestedHeight, Math.max(220, windowHeight - margin * 2));

  const { anchorPosition } = options;

  if (
    anchorPosition &&
    (typeof anchorPosition.x === "number" || typeof anchorPosition.y === "number")
  ) {
    let anchorX =
      typeof anchorPosition.x === "number" && !isNaN(anchorPosition.x)
        ? anchorPosition.x
        : windowWidth / 2;
    let anchorY =
      typeof anchorPosition.y === "number" && !isNaN(anchorPosition.y)
        ? anchorPosition.y
        : windowHeight / 2;

    // Safety guard on web: If anchor coordinates exceed window dimensions and document is scrolled,
    // normalize document coordinates to viewport coordinates.
    if (
      Platform.OS === "web" &&
      typeof window !== "undefined"
    ) {
      if (typeof window.scrollY === "number" && window.scrollY > 0 && anchorY > windowHeight) {
        anchorY = anchorY - window.scrollY;
      }
      if (typeof window.scrollX === "number" && window.scrollX > 0 && anchorX > windowWidth) {
        anchorX = anchorX - window.scrollX;
      }
    }

    const spaceBelow = windowHeight - anchorY - offset - margin;
    const spaceAbove = anchorY - offset - margin;

    // Open upward if space below is insufficient and there is more room above
    const opensUpward = spaceBelow < maxHeight && spaceAbove >= spaceBelow;

    let top: number;
    if (opensUpward) {
      top = Math.max(margin, anchorY - offset - maxHeight);
    } else {
      top = Math.max(margin, Math.min(anchorY + offset, windowHeight - maxHeight - margin));
    }

    // Horizontally center popover around anchor point, clamped inside screen margins
    const idealLeft = Math.round(anchorX - width / 2);
    const minLeft = margin;
    const maxLeft = Math.max(margin, windowWidth - width - margin);
    const left = Math.min(Math.max(minLeft, idealLeft), maxLeft);

    return {
      top,
      left,
      width,
      maxHeight,
      opensUpward,
    };
  }

  // Fallback when no anchor provided: center horizontally, upper third vertically on desktop
  const left = Math.max(margin, Math.round((windowWidth - width) / 2));
  const top =
    windowWidth >= 640
      ? Math.max(
          margin,
          Math.min(Math.round(windowHeight * 0.08), windowHeight - maxHeight - margin)
        )
      : Math.max(margin, Math.round((windowHeight - maxHeight) / 2));

  return {
    top,
    left,
    width,
    maxHeight,
    opensUpward: false,
  };
}
